import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Comment from './Comment';
import { useJournalVariant } from '../JournalVariantContext';

const CommentsContainer = styled('div')`
  margin-bottom: 10px;
  padding-left: ${(props) => (props.isBuildVariant ? '20px' : '60px')};
  padding-top: 20px;
  @media screen and (max-width: 540px) {
    padding-left: ${(props) => (props.isBuildVariant ? '20px' : '30px')};
  }
`;

const CommentList = ({ journalEntry }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { isLoadingComment } = useSelector((state) => state.journal.ui);
  const isBuildVariant = useJournalVariant() === 'build';
  const commentsContainerRef = useRef(null);
  const previousCommentsCountRef = useRef(journalEntry.comments?.length || 0);
  const wasLoadingRef = useRef(false);

  // Scroll to newly created comment when it appears
  useEffect(() => {
    const currentCommentsCount = journalEntry.comments?.length || 0;
    const commentsIncreased = currentCommentsCount > previousCommentsCountRef.current;
    const justFinishedLoading = wasLoadingRef.current && !isLoadingComment;

    if (commentsIncreased && justFinishedLoading && commentsContainerRef.current) {
      // Find the first comment element (newest after sorting) and scroll to it
      const firstComment = commentsContainerRef.current.querySelector('[data-comment-id]');
      if (firstComment) {
        // Small delay to ensure DOM has updated
        setTimeout(() => {
          firstComment.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 200);
      }
    }

    previousCommentsCountRef.current = currentCommentsCount;
    wasLoadingRef.current = isLoadingComment;
  }, [journalEntry.comments, isLoadingComment]);

  const renderCommentsList = () => {
    if (!journalEntry.comments?.length) return null;

    const sortedComments = [...journalEntry.comments].sort((a, b) => {
      const date1 = new Date(a.createdAt);
      const date2 = new Date(b.createdAt);
      return date2 - date1;
    });

    return sortedComments.map((comment) => (
      <Comment
        key={comment.id}
        journalEntry={journalEntry}
        comment={comment}
        currentUser={currentUser}
      />
    ));
  };

  return (
    <CommentsContainer ref={commentsContainerRef} isBuildVariant={isBuildVariant}>
      {renderCommentsList()}
    </CommentsContainer>
  );
};

CommentList.propTypes = {
  journalEntry: PropTypes.shape({
    comments: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  }).isRequired,
};

export default CommentList;
